import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import InstagramFeedSection from "@/components/feature/InstagramFeedSection";
import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import SeoHead from "@/seo/SeoHead";
import { SITE_URL } from "@/seo/siteSeo";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

const ServiceCard = ({ service, idx }: { service: any; idx: number }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      id={`service-${service.id}`}
      className="scroll-mt-28 bg-zinc-900 overflow-hidden border border-zinc-800 hover:border-red-600/40 transition-all duration-300 group"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.55s ease ${(idx % 3) * 0.1}s, transform 0.55s ease ${(idx % 3) * 0.1}s, border-color 0.3s`,
      }}
      {...editableAttrs("services.items", "array")}
    >
      <div className="relative overflow-hidden h-52">
        <img
          src={service.img}
          alt={service.title}
          className={`w-full h-full object-cover ${service.id === "02" ? "object-[50%_35%]" : "object-center"} group-hover:scale-105 transition-transform duration-500`}
          {...editableAttrs(`services.items.${idx}.img`, "image")}
        />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent"></div>
        <span className="absolute top-3 left-4 text-5xl font-bold text-white/10 group-hover:text-red-600/20 transition-colors" style={{ fontFamily: "Rajdhani, sans-serif" }}>
          {service.id}
        </span>
        <div className="absolute bottom-3 right-3 w-10 h-10 flex items-center justify-center bg-red-600/20 border border-red-600/40 group-hover:bg-red-600 transition-all duration-300">
          <i className={`${service.icon} text-red-400 group-hover:text-white text-base`}></i>
        </div>
      </div>
      <div className="p-6">
        <h3
          className="text-white font-bold text-xl mb-3 group-hover:text-red-400 transition-colors"
          style={{ fontFamily: "Rajdhani, sans-serif" }}
          {...editableAttrs(`services.items.${idx}.title`, "text")}
        >
          {service.title}
        </h3>
        <p className="text-zinc-400 text-sm leading-relaxed mb-5" {...editableAttrs(`services.items.${idx}.desc`, "textarea")}>
          {service.desc}
        </p>
        <ul className="flex flex-col gap-2" {...editableAttrs(`services.items.${idx}.detail`, "array")}>
          {service.detail.map((d: string) => (
            <li key={d} className="flex items-center gap-2 text-zinc-300 text-sm">
              <i className="ri-check-line text-red-500 text-sm flex-shrink-0"></i>
              {d}
            </li>
          ))}
        </ul>
        <Link
          to={`/hizmetler/${service.slug}`}
          className="inline-flex items-center gap-2 mt-5 text-red-500 hover:text-red-400 text-sm font-semibold transition-colors"
          style={{ fontFamily: "Rajdhani, sans-serif" }}
        >
          Detay Sayfası
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>
    </div>
  );
};

export default function HizmetlerPage() {
  const { content } = useSiteContent();
  const services = content.services.items;
  const hero = content.services.hero;
  const cta = content.services.cta;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Car Garage Hizmetler",
    itemListElement: services.map((service, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: service.title,
      url: `${SITE_URL}/hizmetler/${service.slug}`,
    })),
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#09090b" }}>
      <SeoHead
        title="Hizmetler"
        description={hero.description}
        path="/hizmetler"
        image={hero.imageSrc}
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="relative pt-36 pb-20 overflow-hidden" {...editableAttrs("services.hero", "object")}>
        <div className="absolute inset-0 z-0">
          <img
            src={hero.imageSrc}
            alt="Hizmetler"
            className="w-full h-full object-cover object-top"
            {...editableAttrs("services.hero.imageSrc", "image")}
          />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-zinc-950/95 via-zinc-950/80 to-zinc-950/60"></div>
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-zinc-950/90 via-transparent to-zinc-950/50"></div>
        </div>
        <div className="absolute left-0 top-0 h-full w-1 bg-red-600 z-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors cursor-pointer">
              Ana Sayfa
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500">Hizmetler</span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {hero.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {hero.title}
          </h1>
          <p className="text-zinc-300 text-lg mt-5 max-w-2xl leading-relaxed">{hero.description}</p>
          <div className="flex items-center gap-3 mt-8">
            <span className="w-8 h-0.5 bg-red-600"></span>
            <span className="text-zinc-400 text-sm">{hero.tagsLine}</span>
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" {...editableAttrs("services.items", "array")}>
            {services.map((service, idx) => (
              <ServiceCard key={service.id} service={service} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      <InstagramFeedSection />

      <section className="py-20 bg-zinc-900" {...editableAttrs("services.cta", "object")}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {cta.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-5" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {cta.title}
            <br />
            <span className="text-red-500">{cta.titleAccent}</span>
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-xl mx-auto">{cta.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/iletisim"
              className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "Rajdhani, sans-serif" }}
            >
              <i className="ri-calendar-check-line text-base"></i>
              {cta.buttonLabel}
            </Link>
            <a
              href={`tel:${cta.phoneLabel.replace(/\D/g, "")}`}
              className="flex items-center justify-center gap-2 border border-white/30 hover:border-red-600 text-white hover:text-red-500 px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "Rajdhani, sans-serif" }}
            >
              <i className="ri-phone-line text-base"></i>
              {cta.phoneLabel}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
